import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useForm, FormProvider } from 'react-hook-form';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { ATSReferenceCheckDataTestIds } from 'data-testids/ATS';
import { SecondaryButton } from '../../styles';
import { SuccessSnackbar, ErrorSnackbar } from 'sharedComponents/Snackbar';
import { ReferenceCheckModal } from '../ReferenceCheckModal';
import { assignEditableReferenceCheckAutomationProperties } from '../ReferenceCheckModal/utils';
import { primaryButtonOverrideSx } from 'sharedComponents/Buttons/styles';
import {
  useQueryGetAutomationsListData,
  useQueryGetSuperAndDistrictAdminsData,
  useQueryGetEmailTemplateListData,
  useQueryGetReferenceTemplateListData,
} from 'features/Automations/api/listQueries';
import { useQueryGetApplicationStatusListData } from 'features/Automations/api/createQueries';
import { ReferenceCheckFormData, ReferenceCheckSuccessData } from '../ReferenceCheckModal/types';
import { Automation } from 'features/Automations/types';
import {
  filterReferenceCheckAutomations,
  filterGeneralAutomations,
} from 'features/Automations/utils';
import { appStatusType } from 'utils/enums';
import auth from 'utils/auth';
import { useAutomationMutations } from 'features/Automations/hooks/useAutomationMutations';
import { DEFAULT_AUTOMATION_ERROR_MESSAGE } from 'hooks/errorHandling/errorHandlingHooks';

interface ReferenceCheckSectionProps {
  jobId: number;
}

export const ReferenceCheckSection: React.FC<ReferenceCheckSectionProps> = ({ jobId }) => {
  const theme = useTheme();
  const [showModal, setShowModal] = useState(false);
  const [deletingAutomationId, setDeletingAutomationId] = useState<number | null>(null);
  const [currentToast, setCurrentToast] = useState<'success' | 'error' | null>(null);

  const formMethods = useForm<ReferenceCheckFormData>({
    defaultValues: {
      id: null,
      emailTemplate: '',
      fromUser: '',
      referenceTemplate: '',
      candidateStatus: '',
    },
    mode: 'onChange',
  });

  const { setValue } = formMethods;
  const { data: automations, isLoading: isLoadingAutomations } =
    useQueryGetAutomationsListData(jobId);
  const automationMutation = useAutomationMutations(jobId);

  const user = auth.getUser();
  const districtId: number | undefined = user?.profile?.district?.id;
  const { data: superAndDistrictAdmins } = useQueryGetSuperAndDistrictAdminsData(districtId);
  const { data: emailTemplates } = useQueryGetEmailTemplateListData(user?.id);
  const { data: applicationStatuses } = useQueryGetApplicationStatusListData(
    districtId,
    Object.values(appStatusType).filter(
      (status): status is number => typeof status === 'number' && status !== appStatusType.draft
    )
  );
  const { data: referenceTemplatesData } = useQueryGetReferenceTemplateListData(districtId);

  const getSenderName = (userId: number) => {
    const sender = superAndDistrictAdmins?.find((admin) => admin.id === userId);
    return sender?.name || 'Unknown User';
  };

  const getEmailTemplateName = (templateId: number) => {
    const template = emailTemplates?.find((template) => template.id === templateId);
    return template?.title || 'Unknown Template';
  };

  const getReferenceTemplateName = (templateId: number) => {
    const template = referenceTemplatesData?.find((template) => template.id === templateId);
    return template?.title || 'Unknown Reference Template';
  };

  const getCandidateStatusName = (statusId: number) => {
    const status = applicationStatuses?.find((status) => status.id === statusId);
    return status?.label || 'Unknown Status';
  };

  const formatAutomationDescription = (automation: Automation): React.ReactNode => {
    const emailTemplateName = automation.email?.email_template_id
      ? getEmailTemplateName(automation.email.email_template_id)
      : 'No Email Template';
    const senderName = automation.email?.sender
      ? getSenderName(automation.email.sender)
      : 'No Sender';
    const referenceTemplateName = automation.action.reference_template_id
      ? getReferenceTemplateName(automation.action.reference_template_id)
      : 'No Reference Template';
    const statusName = automation.trigger.new_status_id
      ? getCandidateStatusName(automation.trigger.new_status_id)
      : 'No Status';

    return (
      <>
        Send email <strong>{emailTemplateName}</strong> from <strong>{senderName}</strong> with
        reference check template <strong>{referenceTemplateName}</strong> when candidate reaches the
        status <strong>{statusName}</strong>
      </>
    );
  };

  const handleSetupClick = (): void => {
    setValue('id', null);
    setValue('emailTemplate', '');
    setValue('fromUser', '');
    setValue('referenceTemplate', '');
    setValue('candidateStatus', '');
    setShowModal(true);
  };

  const handleEditAutomation = (automation: Automation): void => {
    assignEditableReferenceCheckAutomationProperties(automation, setValue);
    setShowModal(true);
  };

  const handleCloseModal = (): void => {
    setShowModal(false);
  };

  const handleSuccess = (_data: ReferenceCheckSuccessData): void => {
    setShowModal(false);
    setCurrentToast('success');
  };

  const handleDirectDelete = async (automationId: number): Promise<void> => {
    setDeletingAutomationId(automationId);

    try {
      const currentAutomations = automations || [];
      const generalAutomations = filterGeneralAutomations(currentAutomations);
      const existingReferenceCheckAutomations = filterReferenceCheckAutomations(currentAutomations);

      const updatedReferenceCheckAutomations = existingReferenceCheckAutomations.filter(
        (automation) => automation.id !== automationId
      );

      const updatedAutomations = [...generalAutomations, ...updatedReferenceCheckAutomations];

      await automationMutation.mutateAsync(updatedAutomations);
    } catch (error) {
      setCurrentToast('error');
    } finally {
      setDeletingAutomationId(null);
    }
  };

  const referenceCheckAutomations = automations ? filterReferenceCheckAutomations(automations) : [];

  return (
    <Box
      data-testid={ATSReferenceCheckDataTestIds.REFERENCE_CHECK_SECTION}
      sx={{
        marginBottom: theme.spacing(3),
        marginTop: theme.spacing(5),
      }}
    >
      <Typography
        data-testid={ATSReferenceCheckDataTestIds.REFERENCE_CHECK_TITLE}
        variant="h3"
        sx={{
          color: theme.palette.text.primary,
          fontWeight: theme.typography.fontWeightMedium,
          marginBottom: theme.spacing(1),
        }}
      >
        Reference Checks
      </Typography>

      <Typography
        data-testid={ATSReferenceCheckDataTestIds.REFERENCE_CHECK_DESCRIPTION}
        variant="body1"
        sx={{
          color: theme.palette.text.secondary,
          marginBottom: theme.spacing(2),
          lineHeight: 1.5,
        }}
      >
        Automatically send reference check emails to a candidate&apos;s reference contacts when they
        reach a specific status.
      </Typography>

      {isLoadingAutomations ? (
        <Typography variant="body2">Loading automations...</Typography>
      ) : referenceCheckAutomations.length > 0 ? (
        <Box sx={{ marginBottom: theme.spacing(2) }}>
          {referenceCheckAutomations.map((automation) => (
            <Box
              key={automation.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: theme.spacing(2),
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: theme.spacing(1),
                marginBottom: theme.spacing(1),
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body1"
                  sx={{ color: theme.palette.text.primary, lineHeight: 1.5 }}
                >
                  {formatAutomationDescription(automation)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: theme.spacing(1) }}>
                <IconButton
                  data-testid={ATSReferenceCheckDataTestIds.EDIT_BUTTON}
                  onClick={() => handleEditAutomation(automation)}
                  size="small"
                  sx={{ color: theme.palette.primary.main }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  data-testid={ATSReferenceCheckDataTestIds.DELETE_ICON_BUTTON}
                  onClick={() => handleDirectDelete(automation.id!)}
                  size="small"
                  disabled={deletingAutomationId === automation.id}
                  sx={{
                    color: theme.palette.error.main,
                    '&:hover': {
                      backgroundColor: theme.palette.error.light,
                    },
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <SecondaryButton
          data-testid={ATSReferenceCheckDataTestIds.SET_UP_AUTO_REFERENCE_BUTTON}
          onClick={handleSetupClick}
          variant="outlined"
          sx={{
            ...primaryButtonOverrideSx(theme),
          }}
        >
          + Add an Automated Reference Check
        </SecondaryButton>
      )}

      <FormProvider {...formMethods}>
        <ReferenceCheckModal
          open={showModal}
          onClose={handleCloseModal}
          onSuccess={handleSuccess}
          jobId={jobId}
        />
      </FormProvider>

      <SuccessSnackbar
        open={currentToast === 'success'}
        message="Successfully saved"
        onClose={() => setCurrentToast(null)}
        dataTestId={ATSReferenceCheckDataTestIds.SUCCESS_TOAST}
        sx={{
          '& .MuiAlert-root': {
            padding: `${theme.spacing(0.75)} ${theme.spacing(2)}`,
            borderRadius: theme.spacing(0.5),
          },
        }}
      />

      <ErrorSnackbar
        open={currentToast === 'error'}
        message={DEFAULT_AUTOMATION_ERROR_MESSAGE}
        onClose={() => setCurrentToast(null)}
        dataTestId={ATSReferenceCheckDataTestIds.ERROR_TOAST}
        sx={{
          '& .MuiAlert-root': {
            padding: `${theme.spacing(0.75)} ${theme.spacing(2)}`,
            borderRadius: theme.spacing(0.5),
          },
        }}
      />
    </Box>
  );
};
